import styled from "styled-components";

import { PNG } from "@ryde/assets";
import type { IStyledAttributes, RideDelayStatusEnum } from "@ryde/shared";
import {
  blueGreyMui,
  deepPurpleMui,
  greyMui,
  MOBILE_BREAKPOINT,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import { RideStatusDelayToColor } from "../../utils";

const STICKY_SECOND_CHILD_IDENT = "70px";

export const TableTagStyled = styled.table.attrs<IStyledAttributes>({
  "data-e2e": "table-root",
})<{ $isSized?: boolean }>`
  padding-bottom: 5px;
  /* A fixed-layout table is never narrower than its columns, so 1px renders the resized widths exactly
     instead of stretching them; fit-content still stretches in Firefox. */
  width: ${({ $isSized }) => ($isSized ? "1px" : "100%")};
  table-layout: fixed;
  white-space: nowrap;
  overflow-x: auto;
  border: 0;
  border-collapse: separate;
  border-spacing: 0;
  padding-inline-end: 10px;
  thead tr:first-child th:first-child {
    border-start-start-radius: 3px;
  }
  tbody tr:last-child td:first-child {
    border-end-start-radius: 3px;
  }
  thead tr:first-child th:last-child {
    border-start-end-radius: 3px;
    border-inline-end: 1px solid ${greyMui._300};
  }

  thead tr:first-child {
    width: 40px;
  }
  tbody tr:last-child td:last-child {
    border-end-end-radius: 3px;
  }
  tbody tr td:last-child {
    border-inline-end: 1px solid ${greyMui._300};
  }
  tbody tr:last-child td {
    border-bottom: 1px solid ${greyMui._300};
  }
  tr th {
    background-color: ${rydeColors.white};
    height: 37px;
  }
  tr td {
    background-color: ${rydeColors.white};
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 37px;
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  tr td:not(.details_panel) {
    overflow: hidden;
  }

  tbody tr:hover td {
    background-color: ${rydeColors.smoke};
  }

  tbody tr.highlighted td {
    background-color: ${rydeColors.rowHighlight};
  }

  tbody tr.highlighted:hover td {
    background-color: ${deepPurpleMui._50};
  }

  tbody tr.dragover:hover td {
    background-color: ${blueGreyMui._400};
  }

  tbody tr.expanded td {
    background-color: ${greyMui._200};
  }

  tbody tr.expanded:hover td {
    background-color: ${greyMui._300};
  }

  tbody tr.hover_drag_row {
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  }

  tbody tr.hover_drag_row td {
    background-color: gainsboro;
  }

  tbody tr td:first-child {
    border-top: none;
    border-bottom: none;
    width: 3px;
    padding: 0;
    margin: 0;
  }

  tbody tr td:first-child:not(.custom_left_line) {
    background: url(${PNG.TableLeftBgMuiPNG}) repeat-y;
  }

  tbody tr td:first-child.custom_left_line:has(.darkRed) {
    background: ${redMui._900};
  }

  tbody tr td:first-child.custom_left_line:has(.red) {
    background: url(${PNG.TableLeftBgMuiRedPNG}) repeat-y;
  }

  tbody tr td:first-child.custom_left_line:has(.orange) {
    background: url(${PNG.TableLeftBgMuiOrangePNG}) repeat-y;
  }

  tbody tr td:first-child.custom_left_line:has(.violet) {
    background: url(${PNG.TableLeftBgMuiVioletPNG}) repeat-y;
  }

  tbody tr td:first-child.custom_left_line:has(.normal) {
    background: url(${PNG.TableLeftBgMuiPNG}) repeat-y;
  }

  tr th.col_idempty {
    border-top: none;
    border-bottom: none;
    padding: 0;
    margin: 0;
    width: 2px;
  }

  tr th.col_idempty:not(.custom_left_line_head) {
    background: url(${PNG.TableLeftBgMuiPNG}) repeat-y;
  }

  tr th.col_idempty.custom_left_line_head:has(.darkRed) {
    background: ${redMui._900};
  }

  tr th.col_idempty.custom_left_line_head:has(.red) {
    background: url(${PNG.TableLeftBgMuiRedPNG}) repeat-y;
  }

  tr th.col_idempty.custom_left_line_head:has(.orange) {
    background: url(${PNG.TableLeftBgMuiOrangePNG}) repeat-y;
  }

  tr th.col_idempty.custom_left_line_head:has(.violet) {
    background: url(${PNG.TableLeftBgMuiVioletPNG}) repeat-y;
  }

  tr th.col_idempty.custom_left_line_head:has(.normal) {
    background: url(${PNG.TableLeftBgMuiPNG}) repeat-y;
  }

  tr th.col_id {
    width: 70px;
  }

  tr th.col_active {
    width: 60px;
  }

  tr.drag_over {
    outline: 3px solid ${rydeColors.dragBorderColor};
    box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.25);
  }
  tr.drag_not_allowed {
    opacity: 0.3;
  }

  tr.details_panel_row_hidden_during_drag {
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }

  td.details_panel_cell_hidden_during_drag {
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    visibility: hidden !important;
    pointer-events: none !important;
    padding: 0 !important;
    border: none !important;
  }

  tr.details_panel_row_hidden_during_passenger_drag {
    display: none !important;
    height: 0 !important;
  }

  tr.transparent > td:not(:first-child) {
    opacity: 0.3;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    thead tr:first-child th:first-child {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: 0;
      .th_content {
        width: 3px;
      }
    }
    thead tr:first-child th:nth-child(2) {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: 3px;
      width: ${STICKY_SECOND_CHILD_IDENT};
      .th_content {
        min-width: 29px;
      }
    }
    thead tr:first-child th:nth-child(3) {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: ${STICKY_SECOND_CHILD_IDENT};
      .th_content {
        min-width: 48px;
      }
    }

    tbody tr td:first-child {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: 0;
    }
    tbody tr td:nth-child(2) {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: 3px;
    }
    tbody tr td:nth-child(3) {
      position: sticky;
      z-index: ${ScreenDepth.STICKY_LEFT_CELL};
      inset-inline-start: ${STICKY_SECOND_CHILD_IDENT};
    }
    tbody tr.transparent > td:nth-child(-n + 3) {
      opacity: 1;
    }
  }
`;

export const TableBodyStyled = styled.tbody.attrs<IStyledAttributes>({
  "data-e2e": "table-body",
})``;

export const TableHeadStyled = styled.thead.attrs<IStyledAttributes>({
  "data-e2e": "table-head",
})`
  padding: 2px;
`;

export const GroupingAreaStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "table-grouping-area",
})<{ $delayStatus?: RideDelayStatusEnum }>`
  color: ${({ $delayStatus }) =>
    $delayStatus ? RideStatusDelayToColor[$delayStatus] : blueGreyMui._800};
  font-size: 14px;
  font-family: "Roboto Condensed", "Roboto", sans-serif;
  font-weight: 500;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  line-height: normal;
  height: 22px;
  label.rows_count {
    margin-inline-start: 8px;
    color: ${blueGreyMui._300};
  }
  svg.collapse_toggle {
    margin-inline-end: 8px;
    cursor: pointer;
  }
  svg.reset_grouping {
    margin-inline-start: 14px;
    cursor: pointer;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-inline-start: 15px;

    .group-area__text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;
